import { cva, type VariantProps } from 'class-variance-authority';

/** Design-token `text-*` classes for each typography variant. */
export const typographyVariantSizeClass = {
    'display-lg': 'text-display-lg',
    display: 'text-display',
    'display-sm': 'text-display-sm',
    h1: 'text-h1',
    h2: 'text-h2',
    h3: 'text-h3',
    h4: 'text-h4',
    h5: 'text-h5',
    span: 'text-body-md',
    p: 'text-body-md',
    large: 'text-body-lg',
    small: 'text-body-sm',
    caption: 'text-caption',
    'product-title': 'text-h3',
    'product-price': 'text-body-lg',
    'product-description': 'text-body-md',
    'recommendation-title': 'text-body-sm',
} as const;

export type TypographyVariantWithSizeClass = keyof typeof typographyVariantSizeClass;

export function getTypographyVariantSizeClass(variant: TypographyVariantsProps['variant']): string | undefined {
    if (!variant) {
        return undefined;
    }

    if (variant in typographyVariantSizeClass) {
        return typographyVariantSizeClass[variant as TypographyVariantWithSizeClass];
    }

    return undefined;
}

export const typographyVariants = cva('', {
    variants: {
        variant: {
            // --- Design spec variants (from Figma / SpaceNK token scale) ---
            // Display
            'display-lg': 'font-display uppercase font-medium',
            display: 'font-display uppercase font-medium',
            'display-sm': 'font-display uppercase font-medium',
            // Headings
            h1: 'font-display text-h1 uppercase font-medium',
            h2: 'font-display text-h2 uppercase font-medium',
            h3: 'font-display text-h3 uppercase font-medium',
            h4: 'font-display text-h4 uppercase font-normal tracking-[1.28px] lg:tracking-[1.44px] text-base lg:text-[18px] leading-[19px]',
            h5: 'font-display text-h5 uppercase font-medium',
            // Body
            span: 'font-sans',
            p: 'font-sans',
            large: 'font-sans font-semibold',
            small: 'font-sans font-medium',
            caption: 'font-sans',

            // --- Utility variants (not in Figma designs) ---
            muted: 'font-sans text-body-sm text-muted-foreground',
            lead: 'font-sans text-body-lg text-muted-foreground',
            blockquote: 'mt-6 border-l-2 pl-6 italic',
            'inline-code': 'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-medium',
            'product-title': 'font-display uppercase font-medium',
            'product-price': 'font-sans font-medium',
            'product-description': 'font-sans',
            'recommendation-title': 'font-display uppercase font-medium tracking-[0.12em]',
            // --- Amiri brand styles ---
            'amiri-display': 'font-serif text-[36px] leading-[40px] lg:text-[48px] lg:leading-[52px]',
            'amiri-h1': 'font-serif text-[28px] leading-[33px] lg:text-[30px] lg:leading-[40px]',
            'amiri-subheading': 'font-serif text-[20px] leading-[24px] lg:text-[24px] lg:leading-[30px]',
            'amiri-body': 'font-serif text-[16px] leading-[19px]',
        },
        align: {
            left: 'text-left',
            center: 'text-center',
            right: 'text-right',
        },
        weight: {
            light: 'font-light',
            regular: 'font-normal',
            medium: 'font-medium',
            semibold: 'font-semibold',
        },
    },
    defaultVariants: {
        variant: 'p',
        align: 'left',
    },
});

export type TypographyVariantsProps = VariantProps<typeof typographyVariants>;
